import { DataGridPro, LicenseInfo, GridToolbar } from "@mui/x-data-grid-pro";
import { useState, useMemo } from "react";

import {
  Avatar,
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Snackbar,
  Tooltip,
} from "@common-ui/ui";
import { AddCircle, Edit, DeleteForever } from "@mui/icons-material";

import ContactDialog from "../dialogs/ContactDialog";
import RemovalDialog from "../dialogs/RemoveContactDialog";

import { getAvatarInitials } from "../../../utils";
import checkTableValue from "../../../utils/checkTableValue";

LicenseInfo.setLicenseKey(process.env.REACT_APP_DATA_GRID_LICENSE);

const Contacts = ({
  contacts,
  customerDetails,
  addresses,
  setIsSuccess,
  doesUserHavePermission,
  rowSize,
  isShowTableHeader,
}) => {
  const contactsCount = contacts ? contacts?.length : 0;
  const [contactsRows, setContactsRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [openRemove, setOpenRemove] = useState(false);
  const [mode, setMode] = useState("create");
  const [currentId, setCurrentId] = useState(undefined);
  const [toastOpen, setToastOpen] = useState(false);
  const [pageSize, setPageSize] = useState(rowSize);

  useMemo(() => {
    if (contacts && contacts.length) {
      setContactsRows(contacts.map((a) => ({ ...a, id: a.system_id })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts]);
  const handleClose = () => {
    setCurrentId(undefined);
    setOpen(false);
  };

  const handleCloseToast = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setToastOpen(false);
  };

  const contactsColumns = [
    {
      width: 100,
      field: "avatar",
      headerName: "",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const FN = params.row.first_name + " " + params.row.last_name;
        return <Avatar alt={FN} src={getAvatarInitials(FN)} />;
      },
    },
    {
      flex: 1,
      field: "first_name",
      headerName: "First Name",
      renderCell: (params) => checkTableValue(params?.value),
    },
    {
      flex: 1,
      field: "last_name",
      headerName: "Last Name",
      renderCell: (params) => checkTableValue(params?.value),
    },
    {
      flex: 1,
      field: "title",
      headerName: "Title",
      renderCell: (params) => checkTableValue(params?.value),
    },
    {
      flex: 1,
      field: "email",
      headerName: "Email",
      renderCell: (params) => checkTableValue(params?.value),
    },
    {
      flex: 1,
      field: "phone",
      headerName: "Phone",
      renderCell: (params) => checkTableValue(params?.value),
    },
    {
      field: "id",
      headerName: "Actions",
      width: 230,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Box>
          {doesUserHavePermission && (
            <>
              <Tooltip title="Edit">
                <Edit
                  size="small"
                  onClick={() => {
                    setOpen(true);
                    setMode("edit");
                    setCurrentId(params.id);
                  }}
                />
              </Tooltip>
              <Tooltip title="Remove Work Event">
                <DeleteForever
                  sx={{ marginLeft: "1rem" }}
                  size="small"
                  onClick={() => {
                    setCurrentId(params.id);
                    setOpenRemove(true);
                  }}
                />
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  return (
    <>
      <Card>
        {isShowTableHeader && (
          <CardContent
            sx={{
              display: "flex",
              justifyContent: "space-between",
              padding: ".5rem 1rem",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" component="div">
              Contacts: ({contactsCount})
            </Typography>
            {doesUserHavePermission && (
              <Button
                size="medium"
                type="button"
                variant="text"
                startIcon={<AddCircle />}
                sx={{ ml: 2, color: (theme) => theme.palette.grey[700] }}
                onClick={() => {
                  setMode("create");
                  setOpen(true);
                }}
              >
                Add Contacts
              </Button>
            )}
          </CardContent>
        )}
        <DataGridPro
          getRowId={(row) => row?.system_id}
          sx={dataGrid}
          loading={false}
          density="standard"
          rows={contactsRows}
          columns={contactsColumns}
          pagination
          components={{ Toolbar: GridToolbar }}
          disableSelectionOnClick
          autoHeight={true}
          pageSize={pageSize}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        />
      </Card>
      <ContactDialog
        open={open}
        mode={mode}
        setMode={setMode}
        customerDetails={customerDetails}
        addresses={addresses}
        setIsSuccess={setIsSuccess}
        setToastOpen={setToastOpen}
        handleClose={handleClose}
        currentId={currentId}
      />
      <RemovalDialog
        customerDetails={customerDetails}
        setIsSuccess={setIsSuccess}
        handleClose={() => {
          setCurrentId(undefined);
          setOpenRemove(false);
        }}
        currentId={currentId}
        open={openRemove}
      />
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={handleCloseToast}
      >
        <Alert
          onClose={handleCloseToast}
          severity="success"
          sx={{ width: "100%" }}
          variant="filled"
        >
          Contact added/updated successfully.
        </Alert>
      </Snackbar>
    </>
  );
};

const dataGrid = {
  ".MuiDataGrid-main": {
    background: "#FFFFFF",
  },
  ".MuiDataGrid-columnSeparator": {
    display: "none",
  },
  ".MuiDataGrid-columnHeaders": {
    background: "#F8F8F8",
    border: "1px solid #EBEBEB",
    fontFamily: "Roboto",
    fontSize: "14px",
    lineHeight: "49px",
    color: "#333333",
  },
  ".MuiDataGrid-columnHeaderTitle": {
    fontWeight: 700,
  },
  ".MuiDataGrid-row": {
    color: "#333",
    background: "#FFFFFF",
  },
  ".MuiDataGrid-row:hover": {
    cursor: "pointer",
    background: "rgba(43, 124, 211, 0.2)",
    transition: "100ms",
  },
  ".MuiSvgIcon-root": {
    color: "#B3B3B3",
  },
  ".MuiTypography-root": {
    fontFamily: "Roboto",
    fontSize: "14px",
    lineHeight: "49px",
    color: "#333333",
  },
};

Contacts.defaultProps = {
  rowSize: 25,
  isShowTableHeader: true,
};
export default Contacts;
